import React, { useCallback, useMemo } from 'react';
import { Button, Tree, TreeItem, TreeItemContent } from 'react-aria-components';
import type { Key } from '@react-types/shared';
import { useTreeExpansion } from './useTreeExpansion';
import type {
    TreeControlProps,
    TreeNode
} from './types';
import './styles.css';

function TreeControlComponent<T = unknown>({
    data,
    treeId,
    selectedNodeId = undefined,
    allowExpansion = true,
    onNodeSelected,
    renderAdditionalContent,
    className = 'tree-control',
    showChildCount = true
}: TreeControlProps<T>) {
    const {
        expandedIds,
        setExpandedIds
    } = useTreeExpansion(treeId);

    const nodeById = useMemo(() => {
        const map = new Map<number, TreeNode<T>>();
        const visit = (nodes: TreeNode<T>[]) => {
            for (const node of nodes) {
                map.set(node.id, node);
                visit(node.children);
            }
        };

        visit(data);
        return map;
    }, [data]);

    const forcedExpandedIds = useMemo(() => {
        const ids = new Set<number>();
        const visit = (nodes: TreeNode<T>[]) => {
            for (const node of nodes) {
                if (node.children.some(child => child.id < 0)) {
                    ids.add(node.id);
                }

                visit(node.children);
            }
        };

        visit(data);
        return ids;
    }, [data]);

    const effectiveExpandedKeys = useMemo(() => {
        return new Set<Key>([
            ...Array.from(expandedIds),
            ...Array.from(forcedExpandedIds)
        ]);
    }, [expandedIds, forcedExpandedIds]);

    const handleExpandedChange = useCallback((keys: 'all' | Set<Key>) => {
        if (!allowExpansion || keys === 'all') {
            return;
        }

        setExpandedIds(new Set(Array.from(keys).map(key => Number(key))));
    }, [allowExpansion, setExpandedIds]);

    const toggleNodeExpansion = useCallback((nodeId: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }

            return next;
        });
    }, [setExpandedIds]);

    const handleSelectionChange = useCallback((keys: 'all' | Set<Key>) => {
        if (keys === 'all') {
            return;
        }

        const [selectedKey] = Array.from(keys);
        const selectedNode = selectedKey === undefined ? undefined : nodeById.get(Number(selectedKey));
        onNodeSelected?.(selectedNode);
    }, [nodeById, onNodeSelected]);

    const renderNode = (node: TreeNode<T>): React.ReactNode => {
        const hasChildren = node.children.length > 0;

        return (
            <TreeItem
                key={node.id}
                id={node.id}
                textValue={node.name}
                value={node}
                hasChildItems={hasChildren}
                className="tree-node"
            >
                <TreeItemContent>
                    {({ isExpanded, hasChildItems }) => (
                        <div
                            className={`tree-item ${hasChildren ? 'expandable' : ''} selectable`}
                            onClick={(event) => {
                                if (
                                    !allowExpansion
                                    || !hasChildren
                                    || forcedExpandedIds.has(node.id)
                                    || (event.target instanceof Element && event.target.closest('button, input, .tree-node-actions'))
                                ) {
                                    return;
                                }

                                toggleNodeExpansion(node.id);
                            }}
                        >
                            {hasChildItems && allowExpansion ? (
                                <Button slot="chevron" className="tree-expand-icon tree-expand-button">
                                    <span className={`tree-chevron ${isExpanded ? 'expanded' : ''}`}>
                                        {'>'}
                                    </span>
                                </Button>
                            ) : (
                                <span className="tree-expand-icon">&nbsp;</span>
                            )}

                            <div className="tree-name">
                                {node.name}
                                {showChildCount && hasChildren && (
                                    <span className="tree-count">({node.children.length})</span>
                                )}
                            </div>

                            {renderAdditionalContent?.(node, selectedNodeId)}
                        </div>
                    )}
                </TreeItemContent>

                {hasChildren && node.children.map(child => renderNode(child))}
            </TreeItem>
        );
    };

    return (
        <Tree
            aria-label="Дерево категорий"
            className={className}
            expandedKeys={effectiveExpandedKeys}
            onExpandedChange={handleExpandedChange}
            selectedKeys={selectedNodeId === undefined ? [] : [selectedNodeId]}
            onSelectionChange={handleSelectionChange}
            selectionMode="single"
        >
            {data.map(node => renderNode(node))}
        </Tree>
    );
}

const TreeControl = React.memo(TreeControlComponent) as typeof TreeControlComponent;

export default TreeControl;
